/**
 * 店舗オーナー向け初見デバイス承認 gate (#867 v3+).
 *
 * 顧客側 `useDeviceApprovalGate` (#819) の店舗版。新端末からの login を
 * trusted device 承認まで block する状態を提供。
 *
 * - `enabled` (= 店舗オーナーが 2FA enrolled / TOTP or WebAuthn) が true の
 *   ときだけ fetch / poll / block する
 * - fail-open: status 取得失敗 / device-id 取得不可は「block しない」側に倒す
 * - block 述語: `exists && !trusted && !denied`(記録済かつ未信頼の真の新端末のみ)
 * - waiting 中は poll し、trusted 端末側で承認されたら自動解除
 */
import { useEffect, useSyncExternalStore } from 'react'
import { useStoreOwner } from '../store/useStoreOwner'
import { api } from '../services/api'
import { getDeviceId } from '../lib/deviceId'

type DeviceStatus = { exists: boolean; trusted: boolean; denied: boolean }

type GateState = {
  status: DeviceStatus | null
  loading: boolean
}

const SAFE_OPEN: DeviceStatus = { exists: false, trusted: true, denied: false }

let state: GateState = { status: null, loading: false }
const listeners = new Set<() => void>()
let pollTimer: ReturnType<typeof setInterval> | null = null

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): GateState {
  return state
}

function isWaiting(s: DeviceStatus | null): boolean {
  return !!s && s.exists === true && s.trusted === false && s.denied === false
}

function statusEqual(a: DeviceStatus | null, b: DeviceStatus | null): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.exists === b.exists && a.trusted === b.trusted && a.denied === b.denied
}

function setState(next: Partial<GateState>): void {
  if (
    (next.status === undefined || statusEqual(next.status, state.status)) &&
    (next.loading === undefined || next.loading === state.loading)
  ) {
    return
  }
  state = {
    status: next.status !== undefined ? next.status : state.status,
    loading: next.loading !== undefined ? next.loading : state.loading,
  }
  for (const l of listeners) l()
}

function stopPolling(): void {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function ensurePolling(): void {
  if (pollTimer) return
  pollTimer = setInterval(() => {
    void evaluate()
  }, 4000)
}

let fetchInFlight = false

async function evaluate(): Promise<void> {
  if (fetchInFlight) return
  fetchInFlight = true
  if (state.status === null) setState({ loading: true })
  try {
    let did = ''
    try {
      did = getDeviceId()
    } catch {
      did = ''
    }
    if (!did) {
      stopPolling()
      setState({ status: SAFE_OPEN, loading: false })
      return
    }
    const s = await api.getStoreDeviceStatus(did).catch(() => null)
    if (!s) {
      setState({ status: SAFE_OPEN, loading: false })
      return
    }
    const status: DeviceStatus = {
      exists: !!s.exists,
      trusted: !!s.trusted,
      denied: !!s.denied,
    }
    setState({ status, loading: false })
    if (isWaiting(status)) ensurePolling()
    else stopPolling()
  } catch {
    stopPolling()
    setState({ status: SAFE_OPEN, loading: false })
  } finally {
    fetchInFlight = false
  }
}

/** trusted 端末で承認された後の楽観セット. */
export function markStoreDeviceApproved(): void {
  stopPolling()
  setState({ status: { exists: true, trusted: true, denied: false }, loading: false })
}

/** Logout 時に呼ぶ — cache クリア + poll 停止. */
export function resetStoreDeviceApprovalGate(): void {
  stopPolling()
  setState({ status: null, loading: false })
}

/** 手動再確認 (待機 modal のボタン用). */
export function recheckStoreDeviceApproval(): void {
  void evaluate()
}

export function useStoreDeviceApprovalGate(enabled: boolean): {
  requiresApproval: boolean
  trusted: boolean
  denied: boolean
  loading: boolean
} {
  const isStoreLoggedIn = useStoreOwner((s) => s.isStoreLoggedIn)
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    if (!enabled || !isStoreLoggedIn) {
      if (snapshot.status !== null) resetStoreDeviceApprovalGate()
      return
    }
    if (snapshot.status === null && !snapshot.loading) {
      void evaluate()
    }
  }, [enabled, isStoreLoggedIn, snapshot.status, snapshot.loading])

  const s = enabled ? snapshot.status : null
  return {
    requiresApproval: enabled && isWaiting(s),
    trusted: !!s && s.trusted === true,
    denied: !!s && s.denied === true,
    loading: enabled ? snapshot.loading : false,
  }
}
